import { apolloClient } from '@/apollo';
import { v4 as randomUUID } from 'uuid';
import { GET_UPLOAD_URL } from './graphql';

export const isDefined = <T>(value: T | null | undefined): value is T =>
	value !== undefined && value !== null;

const moneyFormatter = new Intl.NumberFormat('en-AU', {
	style: 'currency',
	currency: 'AUD',
	maximumFractionDigits: 2,
	minimumFractionDigits: 2,
});

export const formatStripePrice = (stripePrice: number | undefined | null) => {
	if (!isDefined(stripePrice)) {
		return undefined;
	}
	return moneyFormatter.format(stripePrice / 100);
};

export const getNumberOrUndefined = (value: number | null | undefined | string) => {
	if (!isDefined(value)) {
		return undefined;
	}

	if (typeof value === 'number') {
		return value;
	}

	if (typeof value === 'string' && value.trim().length > 0) {
		if (!isNaN(Number(value)) && !isNaN(parseFloat(value))) {
			return parseFloat(value);
		}
	}

	return undefined;
};

export const getNumberOrZero = (value: number | null | undefined | string) => {
	return getNumberOrUndefined(value) ?? 0;
};

export const safeJoin = (values: (string | null | undefined)[], separator: string) => {
	return values.filter(isDefined).join(separator);
};

export const safeSort = <T>(values: T[] | null | undefined, compareFn?: (a: T, b: T) => number) => {
	if (!Array.isArray(values)) {
		return values;
	}

	return [...values].sort(compareFn);
};

export const isActiveFlag = (flag: string | null | undefined | boolean): boolean => {
	if (!isDefined(flag)) {
		return false;
	}

	if (typeof flag === 'boolean') {
		return flag;
	}

	const normalised = flag.trim().toLowerCase();

	return ['true', 'yes', 'on', 'ok', '1'].includes(normalised);
};

export const uploadPhotosToS3 = async (photos: string[], ...pathParameters: string[]) => {
	const result = await Promise.allSettled<{ filename: string; type: string; url: string }>(
		photos.map(async (photo) => {
			const { data } = await apolloClient.mutate({
				mutation: GET_UPLOAD_URL,
				variables: { key: `${pathParameters.join('/')}/${randomUUID()}.jpg` },
			});

			// Convert the base64 file URL to an ArrayBuffer
			const photoBytes = await fetch(photo).then((res) => res.arrayBuffer());

			const response = await fetch(data.getUploadUrl.url, {
				method: 'PUT',
				body: photoBytes,
			});

			if (!response.ok) {
				throw new Error('Failed to upload photo');
			}

			return data.getUploadUrl;
		})
	);

	return result;
};
